/**
 * Sports Hooks
 * React Query hooks for sports-related operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  checkSportsEnabled,
  getUserExposures,
  getWalletBalance,
} from '@/services/sports/bettingService';
import {
  getEventsBySportsID,
  getAllMatches,
  getInPlayMatches,
} from '@/services/sports/sportsApiService';

/**
 * Hook to check if sports is enabled
 */
export const useSportsEnabled = () => {
  return useQuery({
    queryKey: ['sportsEnabled'],
    queryFn: checkSportsEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

/**
 * Hook to fetch events by sports ID
 */
export const useEventsBySportsID = (sportsId, enabled = true) => {
  return useQuery({
    queryKey: ['eventsBySportsID', sportsId],
    queryFn: () => getEventsBySportsID(sportsId),
    enabled: enabled && !!sportsId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for live data
    retry: 2,
  });
};

/**
 * Hook to fetch all matches
 */
export const useAllMatches = () => {
  return useQuery({
    queryKey: ['allMatches'],
    queryFn: getAllMatches,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    retry: 2,
  });
};

/**
 * Hook to fetch in-play matches
 */
export const useInPlayMatches = () => {
  return useQuery({
    queryKey: ['inPlayMatches'],
    queryFn: getInPlayMatches,
    staleTime: 1 * 60 * 1000, // 1 minute (more frequent for live matches)
    refetchInterval: 15 * 1000, // Refetch every 15 seconds for live data
    retry: 2,
  });
};

/**
 * Hook to fetch user exposures
 */
export const useUserExposures = (userId, enabled = true) => {
  return useQuery({
    queryKey: ['userExposures', userId],
    queryFn: () => getUserExposures(userId),
    enabled: enabled && !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    retry: 2,
  });
};

/**
 * Hook to fetch wallet balance
 */
export const useWalletBalance = (userId, enabled = true) => {
  return useQuery({
    queryKey: ['walletBalance', userId],
    queryFn: () => getWalletBalance(userId),
    enabled: enabled && !!userId,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 10 * 1000, // Refetch every 10 seconds
    retry: 2,
  });
};

/**
 * Hook to invalidate sports-related queries
 */
export const useInvalidateSports = () => {
  const queryClient = useQueryClient();

  return {
    invalidateMatches: () => {
      queryClient.invalidateQueries({ queryKey: ['allMatches'] });
      queryClient.invalidateQueries({ queryKey: ['inPlayMatches'] });
    },
    invalidateEvents: (sportsId) => {
      queryClient.invalidateQueries({ queryKey: ['eventsBySportsID', sportsId] });
    },
    invalidateExposures: (userId) => {
      queryClient.invalidateQueries({ queryKey: ['userExposures', userId] });
    },
    invalidateWallet: (userId) => {
      queryClient.invalidateQueries({ queryKey: ['walletBalance', userId] });
    },
  };
};
